import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { http, registerPortalAuthHandlers, setPortalAccessToken } from '@/lib/http/apiFetcher';
import { PortalAuthContext } from './PortalAuthContext';
import type {
  ClientUserRole,
  PortalAmbiguousClientHint,
  PortalAuthContextValue,
  PortalLoginError,
  PortalUser,
} from './types';

interface LoginApiResponse {
  accessToken: string;
  accessExpiresAt: string;
  user: { id: string; clientId: string; email: string; fullName: string | null; role: ClientUserRole };
  tokenType: string;
}

interface AmbiguousApiResponse {
  clients: { clientId: string; clientCode: string; clientName: string }[];
}

/**
 * Owns the client-user session in memory. Parallels the admin AuthProvider
 * but targets /auth/portal/* and stores the token in a dedicated slot so an
 * authenticated admin and an authenticated client-user never share bearer
 * tokens across tabs.
 */
export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const refreshInFlight = useRef<Promise<string | null> | null>(null);

  const applyTokens = useCallback((accessToken: string, who: PortalUser) => {
    setPortalAccessToken(accessToken);
    setUser(who);
  }, []);

  const clear = useCallback(() => {
    setPortalAccessToken(null);
    setUser(null);
  }, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    if (refreshInFlight.current) return refreshInFlight.current;

    const attempt = (async () => {
      try {
        const { data } = await http.post<LoginApiResponse>('/auth/portal/refresh');
        if ('accessToken' in data && data.accessToken) {
          applyTokens(data.accessToken, data.user);
          return data.accessToken;
        }
        clear();
        return null;
      } catch {
        clear();
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    refreshInFlight.current = attempt;
    return attempt;
  }, [applyTokens, clear]);

  const logout = useCallback(async () => {
    try {
      await http.post('/auth/portal/logout');
    } catch {
      /* ignore */
    }
    clear();
  }, [clear]);

  const login = useCallback(
    async (email: string, password: string, clientIdHint?: string): Promise<PortalLoginError | null> => {
      try {
        const { data } = await http.post<LoginApiResponse | AmbiguousApiResponse>(
          '/auth/portal/login',
          { email, password, clientId: clientIdHint ?? null },
        );
        if ('accessToken' in data) {
          applyTokens(data.accessToken, data.user);
          return null;
        }
        // Ambiguous-email disambiguation payload.
        const hints: PortalAmbiguousClientHint[] = data.clients.map((c) => ({
          clientId: c.clientId,
          clientCode: c.clientCode,
          clientName: c.clientName,
        }));
        return { kind: 'ambiguous', hints };
      } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 401)
          return { kind: 'invalid_credentials', message: 'Email or password is incorrect.' };
        if (status === 403)
          return { kind: 'account_disabled', message: 'This account is disabled. Contact your ClientAdmin.' };
        if (status === 429)
          return { kind: 'lockout', message: 'Too many failed attempts. Try again in a few minutes.' };
        return { kind: 'network', message: 'Could not reach the authentication service.' };
      }
    },
    [applyTokens],
  );

  useEffect(() => {
    registerPortalAuthHandlers({ refresh, logout: clear });
  }, [refresh, clear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setIsBootstrapping(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: PortalAuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isBootstrapping,
    login,
    logout,
    refresh,
  };

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
}
